// AiMark — the ONE provenance marker for a value on a Desk surface (designer spec 2026-08-19,
// widened on the owner's ruling 2026-08-27, placement rebuilt 2026-08-28).
//
// ✦ is the AI mark, on the GLYPH rather than the chip — the data must not shout, the mark does.
// It began as one violet star (08-19: violet because the Desk's status language does not use it
// — green=pass, amber=stale, red=conflict, blue=links) and on 2026-08-27 it grew TWO further
// axes, from three sessions working the same owner ruling on the same day:
//   • a hue PER AGENT (AGENT_HUE) — which agent read it;
//   • `recognised` vs `authored` (ACTION_KA) — read a fact, or composed the text;
//   • a ✎ PERSON tier (sourceMarkFor) — a human recorded it, and who.
// ⚠️ All three are in this file because they were COMPOSED at the merge, not picked between. A
// side-pick here deletes an axis and still compiles, renders and looks correct — you would
// simply never learn that part of the ruling had gone.
//
// 📌 2026-08-28 — A NOTE FOR ANYONE READING THIS FILE OUT OF A LOCAL CHECKOUT. These axes also
// exist verbatim in `.claude/worktrees/meeting-inheritance-attendance-124e34`, a stale detached
// folder where `git status` answers "not a git repository". That folder is a COPY, not a source:
// it is byte-identical to origin/main. Do not "recover" from it and do not treat a local `main`
// as the truth either — the Drive checkout was measured 480 commits behind origin/main on
// 2026-08-28 while still carrying 66 of its own. Compare against `origin/main` or you will
// reintroduce nine days of already-fixed bugs.
//
// ── 2026-08-27: THE MARK ANSWERS TWO QUESTIONS, NOT ONE ─────────────────────────────────────
//
// Owner ruling, in his words: *"whatever is shown must reflect who filled it"* … *"was it AI
// agent or another real agent"* … *"and who"* … *"we need to be consistent in our system"*.
//
// So a mark carries an AXIS and a NAME:
//   • axis — did a MACHINE read this, or did a PERSON record it?
//   • name — WHICH machine (Lynceus reads photographs, Idmon reads the advert's text), or
//     which person.
//
// Two glyphs, and the second is new:
//   ✦ agent hue — an AI agent, named, coloured by WHICH agent (AGENT_HUE below).
//   ✎ primary   — a person recorded it. This is what did not exist: before today a
//                 human-entered value carried NO mark at all, so "recorded by Nino" and
//                 "scraped off myhome" were the same blank on screen.
//
// 🔑 THE DEFAULT IS THE ADVERT, AND THAT IS WHY IT HAS NO MARK. `property_feature` holds
// 785,565 `scrape` rows against 51,880 agent-read ones: marking every scraped value would put a
// glyph on almost every fact on the Desk and the mark would stop carrying information. The rule
// is therefore *the advert is the unmarked baseline; a mark means somebody or something went
// beyond it.* Anyone tempted to "complete" this by marking scrapes too should read that
// sentence first — the sparseness IS the signal.
//
// ⚠️ AND WHICH PORTAL SAID IT IS NOT ANSWERABLE HERE, so the mark never pretends otherwise.
// `property_feature` is keyed (property, feature, source_kind) and keeps no link back to the
// `raw_listing` it was projected from. That is a real gap, recorded rather than papered over
// with a plausible portal name — the same reason the lineage modal's own popover shows the
// source KIND and never a portal.
//
// ⚠️ INTERNAL ONLY (owner ruling 2026-08-19): this renders in the Desk and never on the client
// share pages — core_share.js knows nothing of it, keep it that way.
//
// ⚠️ A HUMAN CORRECTION WINS BY ABSENCE, not by dimming: when a human-tier row exists for the
// same key, the caller must not render the AI mark at all (aiMarkFor returns null). That is the
// ordinary precedence rule made visual — an overridden AI claim has nothing to advertise. Note
// what widening the mark does to that rule and why it is an improvement rather than a conflict:
// the AI mark still disappears, and the HUMAN row now carries its own ✎ in its place, so the
// value stops being anonymous at exactly the moment a person takes responsibility for it.
// 📌 Measured 2026-08-28: that silencing has fired ZERO times in production — no AI row has a
// human answer on the same key. The absent state is designed and has never rendered. Do not
// "verify" it by looking for it on screen.
//
// One component, every surface — no per-surface bespoke logic (the CooperationChips rule).
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useDeliberateBackdropDismiss } from '../../hooks/useDeliberateBackdropDismiss.js'
import { createPortal } from 'react-dom'
import { popoverPos, useCloseOnPageMove, useCanHover } from './primitives.jsx'

// Agent display names. ⚠️ ლინკევსი/იდმონი are my transliterations, flagged to the B845
// Georgian-review list — correct them HERE and every surface follows.
const AGENT_KA = { idmon: 'იდმონი', lynceus: 'ლინკევსი', tiphys: 'ტიფისი', orpheus: 'ორფევსი' }

// ⚠️ ONE HUE PER AGENT (Idmon session's convention, 2026-08-27): idmon violet · lynceus sky ·
// tiphys amber · orpheus fuchsia. Violet remains the DEFAULT for an unnamed AI source, which is
// what this file shipped with — an agent we have no hue for must still render, just anonymously.
// Orpheus takes fuchsia because the other three are spoken for and it is the one remaining hue
// the Desk's status language does not use (green=pass, amber=stale, red=conflict, blue=links).
// ⚠️ amber IS a status colour and tiphys uses it anyway — flagged rather than silently changed,
// because the convention is not mine to redraw.
// 📌 The Lynceus session independently flagged the same amber collision from the other side: the
// DEFECT chips (AgentFacts.jsx) are amber text. A Tiphys-read value on a row beside a defect will
// read as one family. Two sessions, same finding — if it bites, the DEFECT colour is what moves.
const AGENT_HUE = {
  idmon:    'text-violet-600 dark:text-violet-400',
  lynceus:  'text-sky-600 dark:text-sky-400',
  tiphys:   'text-amber-600 dark:text-amber-400',
  orpheus:  'text-fuchsia-600 dark:text-fuchsia-400',
}
const DEFAULT_HUE = 'text-violet-600 dark:text-violet-400'

// THE SEAT (designer 1c, 2026-08-28 — shipped incomplete the first time, see below). A tinted
// 20px disc under the glyph, in the mark's own hue. It is here rather than at a call site
// BECAUSE the first attempt put a hairline on CooperationChips alone and left the equipment row
// — five marks side by side, the surface where the misread is worst — exactly as it was.
// ⚠️ The "+" reading was never about the star. An unpadded symbol hard against a word is where
// every other Desk chip puts its add affordance, so the eye answers "button" before it answers
// "glyph". A seat plus a divider says "a second thing, and it is a mark" without a variant.
const AGENT_SEAT = {
  idmon:    'bg-violet-500/10 dark:bg-violet-400/20',
  lynceus:  'bg-sky-500/10 dark:bg-sky-400/20',
  tiphys:   'bg-amber-500/10 dark:bg-amber-400/20',
  orpheus:  'bg-fuchsia-500/10 dark:bg-fuchsia-400/20',
}
const DEFAULT_SEAT = 'bg-violet-500/10 dark:bg-violet-400/20'
const HUMAN_SEAT = 'bg-primary/10'

// 🚨 TWO KINDS OF PROVENANCE, ONE MARK. `recognised` is what this file was built for: an agent
// READ a fact, and the evidence is a quote or a photo position. `authored` is different — an
// agent COMPOSED the text you are looking at, and there is no per-claim quote to show because
// the whole block is the artefact. Owner ruling 2026-08-27: wherever Orpheus writes a Facebook
// post or a description, the UI must say so.
// ⚠️ The mark still NEVER leaves the Desk (owner, 2026-08-19). The composed post itself carries
// no attribution — an advert is the agency's word, not an agent's byline. This marks the COMPOSE
// SURFACE, not the output.
// `placed` (owner 2026-09-07): Tiphys put the pin on the map — the mark sits on the map tile
// itself instead of an "agent activity" list.
const ACTION_KA = { recognised: 'ამოიცნო', authored: 'დაწერა', placed: 'რუკაზე დადო' }

// ── WHO SAID IT (owner ruling 2026-09-12) ────────────────────────────────────────────────
// "No matter what — it was not said by Idmon. It was said by the AUTHOR. Idmon just extracted
// this information… the same way a crawler just scrapes the data. Agents should be able to see
// exactly what happened: the owner said this in the description, and Idmon extracted it."
//
// This panel had the running order inverted. Its headline named the AGENT („ამოიცნო იდმონმა"),
// so a fact the owner wrote read as the model's claim, and the owner's own sentence sat at the
// bottom as a footnote. An extraction is a TRANSCRIPTION: the assertion belongs to whoever made
// it, and the agent's part is how it travelled.
//
// 🔑 A TEXT FACT HAS AN AUTHOR; A PHOTO FACT DOES NOT. Somebody WROTE „აგენტებმა არ დამირეკოთ";
// nobody "said" that a fireplace exists — a camera saw one. So vision keeps an observational
// headline („ფოტოზე ჩანს") and never borrows an author it does not have. Getting this wrong in
// the other direction would be worse than the bug being fixed: it would put words in an owner's
// mouth that they never wrote.
//
// ⚠️ „განცხადების ავტორმა", NOT „მესაკუთრემ" (owner ruling 2026-09-12, correcting my first
// pass). I had the headline assert the OWNER wrote it wherever `declared_author_role` said so,
// and that is a role we often have not established — whereas "the author of the advert" is
// established by the act of writing it, always, with nothing assumed.
//
// 🔑 AND IT DISSOLVES A DEPENDENCY RATHER THAN ADDING ONE. The owner/agent distinction is
// ITSELF an extraction (Idmon reading „ვარ მეპატრონე"), and it is contradicted on 348 listings
// where the prose and the portal disagree — `author_role_conflict`. Framing every other fact
// with a contested reading would make one extraction the premise of all the rest. Who the
// author IS stays a separate fact with its own mark; it is not the frame for everything else.
// So no `declared_author_role` plumbing is needed for this headline at all.
//
// ⚠️ A SCRAPE SPEAKS FOR THE FORM, NOT FOR A PERSON. A ticked box on ss.ge was not a sentence
// anybody composed, and the owner's own ruling is that a lone portal tick-box is weak evidence
// — so it says „<portal>-ის კომენტარში მითითებულია" (the owner's own wording) and
// attributes nothing to a person.
// What the agent DID with what it found, said the way a crawler's act would be said — because
// they are the same kind of act (owner, above).
const CARRIED_KA = { idmon: 'ამოიცნო', lynceus: 'დაინახა', tiphys: 'დაადგინა' }

export function authorLine(mark) {
  if (!mark) return ''
  // A photograph is not a speaker. Nothing is attributed.
  if (mark.agent === 'lynceus') return 'ფოტოზე ჩანს'
  if (mark.kind === 'scrape' || mark.kind === 'scrape_reshare') {
    // ⚠️ THE OWNER'S OWN WORDING, 2026-09-12 — „ss.ge-ის კომენტარში მითითებულია". Mine read
    // „ფორმაშია მითითებული", which was both a guess at the portal's vocabulary and the wrong
    // verb form. The place a portal carries this is its COMMENT, which is what the site calls
    // the box; and „მითითებულია" is how the sentence ends.
    return mark.portal ? `${mark.portal}-ის კომენტარში მითითებულია` : 'კომენტარში მითითებულია'
  }
  return 'განცხადების ავტორმა დაწერა'
}

// The attribution line: who carried the fact here, and out of what. Deliberately the same
// grammar for an agent and for a scrape.
export function carriedLine(mark) {
  if (!mark) return ''
  const who = AGENT_KA[mark.agent]
  const verb = CARRIED_KA[mark.agent] || ACTION_KA.recognised
  const from = AGENT_READS_KA[mark.agent]
  if (!who) return ''
  return `${verb} ${ergative(who)}${from ? ' ' + from : ''}`
}

// What each named agent READ, which is the part an agent weighing the claim actually needs: a
// photograph can be checked by opening it, a sentence by re-reading the advert. Orpheus has no
// entry on purpose — it AUTHORS rather than reads, and this line only renders for `recognised`.
const AGENT_READS_KA = { idmon: 'განცხადების ტექსტიდან', lynceus: 'ფოტოებიდან', tiphys: 'მისამართებიდან' }

const HUMAN_KINDS = new Set(['owner', 'registry', 'admin', 'agent', 'fill_form'])

// `audit` is human too, and it is deliberately NOT in HUMAN_KINDS above: that set is the
// SILENCING rule (which source kinds outrank an AI reading), and it predates the audit tier.
// Widening it here would change what the AI mark hides, which is a separate decision from what
// the human mark shows. Kept apart on purpose.
const HUMAN_MARK_KINDS = new Set([...HUMAN_KINDS, 'audit'])

// How a human-tier row is described when it carries no name. These are KINDS, not people —
// the mark says what it knows and stops.
const KIND_KA = {
  owner: 'მფლობელმა', registry: 'რეესტრიდან', admin: 'ადმინისტრატორმა',
  agent: 'აგენტმა', fill_form: 'ფორმიდან', audit: 'შემოწმებისას',
}

// The person's name, where the row carries one. `audit` rows store it in `source_version` as
// "feature_correction · <name>" — commit_feature's own format — because `judged_by` is a name
// and not a party, and inventing a party for it would repeat 0439's mistake of conflating who
// RAN something with who ASSERTED it. Everything else keys on `source_party_id`, which this
// component cannot resolve to a name and therefore does not claim to.
function personName(row) {
  const v = String(row?.source_version || '')
  const m = v.match(/·\s*(.+)$/)
  return m ? m[1].trim() : null
}

// ── LYNCEUS'S EVIDENCE ARRIVES IN FOUR SHAPES, AND THE COMMON ONE IS "NOTHING" ───────────────
// 🚨 Measured in production 2026-08-28, over all 4,047 Lynceus rows:
//     551 carry `photo_index`     — the only shape this component used to read
//     566 carry a sentence        — "photo 4: air conditioner, on an exterior wall"
//      52 carry a JSON LIST       — [{saw, photo}, …] across several frames
//   3,013 carry nothing at all
// So 3,496 of 4,047 (86%) opened a panel with a header, a version footer, and NOTHING BETWEEN
// THEM — the one thing the panel exists to show. 483 of those had perfectly readable evidence
// sitting in `instances[0].evidence`, a field nobody read. Idmon has none of this problem: all
// 54,843 of its rows carry a quote.
//
// The rule that follows (designer, 2026-08-28): four states, ONE ladder, one panel shape — the
// evidence slot is always filled, and the empty case is drawn as the PRIMARY case rather than as
// a fallback, because it is the common one. Never drop a string we cannot parse; print it.
const PHOTO_LEAD = /^\s*photo\s+(\d+)\s*[:—-]\s*/i
function readEvidence(inst) {
  const raw = inst?.evidence
  if (typeof raw !== 'string' || !raw.trim()) return null
  const s = raw.trim()
  if (s.startsWith('[')) {
    try {
      const parsed = JSON.parse(s)
      const list = Array.isArray(parsed)
        ? parsed.filter(x => x && x.saw).map(x => ({ saw: String(x.saw), photo: x.photo }))
        : []
      if (list.length) return { list }
    } catch {
      // Unparseable → fall through and print the whole string verbatim. A dropped sighting is
      // worse than an ugly one: the agent can still read it and go look at the photographs.
    }
  }
  const lead = s.match(PHOTO_LEAD)
  return lead
    ? { photo: Number(lead[1]), line: s.slice(lead[0].length) }
    : { line: s }
}

// Derive the mark's payload from a property_feature-shaped row (source_kind, source_agent,
// source_version, instances, last_seen_at). Returns null for non-AI rows — and callers pass
// `siblings` (other rows of the SAME key) so a human answer silences the mark entirely.
export function aiMarkFor(row, siblings = []) {
  if (!row || row.source_kind !== 'ai') return null
  if (siblings.some(s => s && HUMAN_KINDS.has(s.source_kind))) return null
  const agent = row.source_agent || null
  // instances[0] carries the evidence: Idmon's verbatim quote, or Lynceus's photo position.
  const inst = Array.isArray(row.instances) ? row.instances[0] : null
  const ev = readEvidence(inst)
  return {
    agent,
    // The portal, for a scraped row. No author ROLE is carried on purpose — see authorLine:
    // „განცხადების ავტორმა" needs nothing beyond the fact that an advert exists.
    portal: row.portal || null,
    kind: row.source_kind || null,
    version: row.source_version || null,
    date: row.last_seen_at || null,
    quote: inst?.quote || null,
    // A leading "photo N:" in the evidence sentence is a photo index too — the ladder treats
    // the two the same rather than preferring the structured field that is usually absent.
    photo: Number.isFinite(inst?.photo_index) ? inst.photo_index
         : (Number.isFinite(ev?.photo) ? ev.photo : null),
    where: inst?.where_in_frame || (ev && !ev.list ? ev.line : null),
    sightings: ev?.list || null,
  }
}

// THE GENERAL FORM. Give it every row that says the same thing about one key, get back the ONE
// mark to draw — or null when the value came from the advert and needs none.
//
// Precedence is the platform's, not a new one: a person outranks an agent outranks the advert.
// ⚠️ It returns a single mark on purpose. A value with three sources is still ONE value, and a
// row of glyphs would be a disagreement display — which already exists, is a different control
// (the conflict popover in the lineage modal), and must not be reinvented in a chip.
export function sourceMarkFor(rows = []) {
  const list = (rows || []).filter(Boolean)
  if (!list.length) return null
  const human = list.find(r => HUMAN_MARK_KINDS.has(r.source_kind))
  if (human) {
    return {
      tier: 'human',
      kind: human.source_kind || null,
      name: personName(human),
      date: human.last_seen_at || null,
      fromSibling: !!human.from_sibling,
    }
  }
  const ai = list.find(r => r.source_kind === 'ai')
  if (!ai) return null                    // the advert — the unmarked baseline
  const m = aiMarkFor(ai, list)
  return m && { tier: 'ai', ...m, fromSibling: !!ai.from_sibling }
}

// The sentence the mark says out loud. Exported because a chip may want it as a `title` even
// where the popover would not fit, and two spellings of the same sentence is exactly the drift
// this module exists to prevent.
// 🚨 THE ERGATIVE, and why `name + 'მ'` was wrong. `ამოიცნო` takes an ergative subject, and the
// rule depends on the last letter: a name ending in the nominative -ი DROPS it and takes -მა
// (იდმონი → იდმონმა), any other vowel takes -მ (ნინო → ნინომ), a consonant takes -მა.
// The old code appended a bare -მ to everything and rendered "იდმონიმ" — caught 2026-08-28 by a
// render harness, against the deployed panel which reads "იდმონმა". Latin stays hyphenated.
// ⚠️ Georgian grammar in code: on the B845 review list with AGENT_KA. Correct it HERE.
const VOWELS_KA = 'აეიოუ'
export function ergative(name) {
  const s = String(name || '').trim()
  if (!s) return s
  if (!/[ა-ჿ]$/.test(s)) return `${s}-მ`     // Latin/other script — keep the hyphen
  const last = s[s.length - 1]
  if (last === 'ი') return `${s.slice(0, -1)}მა`
  return VOWELS_KA.includes(last) ? `${s}მ` : `${s}მა`
}

export function markLabel(mark) {
  if (!mark) return ''
  if (mark.tier === 'human') {
    return mark.name ? `ჩაწერა ${ergative(mark.name)}` : `ჩაწერილია ${KIND_KA[mark.kind] || ''}`.trim()
  }
  // ⚠️ `authored` KEEPS THE AGENT AS ITS SUBJECT, and it is the one case that must: Orpheus
  // really is the author of its note, so „დაწერა ორფევსმა" is not the inverted-order bug —
  // it is the truth. Only a RECOGNISED fact has an author who is not the agent.
  if (mark.action === 'authored' || mark.action === 'placed') {
    const who = AGENT_KA[mark.agent] || 'AI'
    return `${ACTION_KA[mark.action]} ${ergative(who)}`
  }
  return authorLine(mark)
}

// 🚨 NOT `toLocaleDateString('ka-GE')`. That is what this file used to call, and the deployed
// Desk rendered `8/28/2026` anyway — the runtime has no ka-GE date data and silently falls back
// to the host locale, which is the US one on the machines this is read on. A locale that can be
// absent is not a format; DD.MM.YYYY is written out so the panel cannot drift by environment.
function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = n => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`
}

const PANEL_W = 264

// The panel's body. ONE renderer for both the anchored popover and the phone sheet — the
// breakpoint switches the CONTAINER, never the content, or the two drift and only one gets
// fixed the next time somebody edits this.
// ⚠️ Exported for the render harness ONLY, and it is not decoration: the panel body renders
// exclusively while open, i.e. behind client state that server rendering never reaches. Asserting
// the derived payload instead would repeat the mistake this component was built out of — the
// four Lynceus states differ by which BRANCH draws, not by what aiMarkFor returns. Not a public
// surface; every real caller uses the default export.
export function MarkBody({ mark, human, hue, glyph, label, big = false }) {
  const [showBuild, setShowBuild] = useState(false)
  // Four evidence states, one ladder, read in order: sighting list → sentence → photo index →
  // nothing. Every branch fills the same slot between header and footer.
  const evidence = (() => {
    if (mark.quote) return <span className="block italic mb-1.5">„{mark.quote}"</span>
    if (mark.sightings?.length) {
      return (
        <span className="block mb-1.5">
          <span className="block text-muted-foreground mb-1">{mark.sightings.length} ფოტოზე</span>
          <span className="block max-h-24 overflow-y-auto overscroll-contain">
            {mark.sightings.map((s, i) => (
              <span key={i} className="flex items-baseline gap-1.5 py-0.5">
                {Number.isFinite(s.photo) && (
                  <span className="shrink-0 rounded bg-muted px-1 text-[10px] tabular-nums">{s.photo}</span>
                )}
                <span>{s.saw}</span>
              </span>
            ))}
          </span>
        </span>
      )
    }
    if (mark.photo != null) {
      return (
        <span className="block mb-1.5">
          <span className="rounded bg-muted px-1 text-[10px] tabular-nums mr-1">ფოტო {mark.photo}</span>
          {mark.where || ''}
        </span>
      )
    }
    if (mark.where) return <span className="block mb-1.5">{mark.where}</span>
    // `placed` (Tiphys put the pin) reads no photograph — the Lynceus sentence below was wrong for it (owner 2026-10-28)
    if (human || mark.action === 'authored' || mark.action === 'placed') return null
    // The common case. The panel cannot show evidence it does not have, so it stops pretending
    // and says which half is missing — the claim was made, only its address wasn't recorded.
    // ⚠️ "not recorded", never "could not verify": Lynceus is confirm-only, and the two are
    // different claims about the world.
    return (
      <span className="block mb-1.5 text-muted-foreground">
        ფოტოებზე დაინახა; რომელ ფოტოზე — ჩაწერილი არ არის.
      </span>
    )
  })()

  return (<>
    <span className={`block font-semibold ${hue} mb-1`}>{glyph} {label}</span>
    {mark.note && <span className="block mb-1">{mark.note}</span>}
    {/* ── THE ORDER IS THE POINT (owner ruling 2026-09-12) ─────────────────────────────
        What happened, in the order it happened: the author asserted it, these were their
        words, and then an agent carried it here. The old order put the agent first and the
        author's sentence last, which read as the model's claim rather than the owner's.
        The words come BEFORE the attribution now — they are the fact, not a footnote. */}
    <span className={big ? 'block text-[15px] leading-snug' : 'block'}>{evidence}</span>
    {/* WHO CARRIED IT. Demoted to attribution and given the same grammar a crawler would get,
        because extracting and scraping are the same kind of act. Silent for the person tier
        (a human needs no reading method) and for `authored`, where the agent is already the
        headline and nothing was read. */}
    {!human && mark.action !== 'authored' && carriedLine(mark) && (
      <span className="block text-muted-foreground mb-1">{carriedLine(mark)}</span>
    )}
    {/* A fact read off a SIBLING advert's photographs belongs to the same flat and not to
        this card's own advert. Saying so is the same "inherited value" honesty the price
        and address already practise — the alternative is presenting a neighbour advert's
        frame as this card's own. */}
    {mark.fromSibling && (
      <span className="block text-muted-foreground mb-1">იმავე ბინის სხვა განცხადებიდან</span>
    )}
    {/* FOOTER (designer, 2026-08-28). The DATE is the only metadata an agent judges by, so it
        stays visible. The model id and the two prompt versions are reproducibility data — three
        build identifiers that used to outweigh the quote they sat under — so they collapse.
        ⚠️ They WRAP rather than truncate when opened: a cut-off build id is worse than a hidden
        one, because it looks like a whole one. */}
    <span className="flex items-baseline justify-between gap-2 text-muted-foreground">
      <span className="tabular-nums">{fmtDate(mark.date)}</span>
      {mark.version && (
        <button type="button" className="shrink-0 hover:text-foreground underline-offset-2 hover:underline"
          aria-expanded={showBuild}
          onClick={e => { e.stopPropagation(); setShowBuild(v => !v) }}>
          დეტალები {showBuild ? '▴' : '▾'}
        </button>
      )}
    </span>
    {showBuild && mark.version && (
      <span className="block mt-1 pt-1 border-t border-border/50 font-mono text-[10px] leading-relaxed break-words text-muted-foreground">
        {mark.version}
      </span>
    )}
  </>)
}

export default function AiMark({ mark, className = '' }) {
  const [pos, setPos] = useState(null)     // anchored panel — a measured {top|bottom,left,maxHeight}
  const [sheet, setSheet] = useState(false) // phone — a bottom sheet, no measurement
  const ref = useRef(null)
  const closeT = useRef(null)
  const canHover = useCanHover()

  const cancelClose = () => { if (closeT.current) { clearTimeout(closeT.current); closeT.current = null } }
  const scheduleClose = () => { cancelClose(); closeT.current = setTimeout(() => setPos(null), 120) }
  const close = useCallback(() => { cancelClose(); setPos(null); setSheet(false) }, [])
  const sheetBackdropDismiss = useDeliberateBackdropDismiss(close)
  // Closes when the PAGE moves, never when the panel's own body is scrolled — the ref must
  // reach the panel element or the guard degrades to close-on-any-scroll (see primitives).
  const panelRef = useCloseOnPageMove(!!pos, () => setPos(null))

  useEffect(() => () => cancelClose(), [])
  useEffect(() => {
    if (!sheet && !pos) return
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); close() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [sheet, pos, close])

  if (!mark) return null
  // Rows produced by aiMarkFor carry no `tier`; they are AI by construction. Defaulting here
  // rather than at every call site keeps every existing caller working untouched.
  const human = mark.tier === 'human'
  const label = markLabel(mark.tier ? mark : { tier: 'ai', ...mark })
  // The person tier keeps ONE colour: a human is a human, and splitting them by name would be a
  // second identity system on a surface that already has too few pixels. The AGENT tier is where
  // identity matters, because the agents differ in WHAT THEY READ — or, for Orpheus, in the fact
  // that they wrote rather than read.
  const hue = human ? 'text-primary' : (AGENT_HUE[mark.agent] || DEFAULT_HUE)
  const glyph = human ? '✎' : '✦'
  const seat = human ? HUMAN_SEAT : (AGENT_SEAT[mark.agent] || DEFAULT_SEAT)
  const body = <MarkBody mark={mark} human={human} hue={hue} glyph={glyph} label={label} />

  // ── PLACEMENT (rebuilt 2026-08-28) ─────────────────────────────────────────────────────────
  // What it used to be: `absolute z-[70] bottom-full left-1/2 -translate-x-1/2 w-64` — no
  // measurement, so no flip, no clamp, and no escape from its container. Three failures came out
  // of that one line, all of them on screen at once:
  //   1. ALWAYS UP covered the rows above the mark — in the facts table those are precisely the
  //      values the agent is comparing against.
  //   2. The lineage modal's panel is `overflow-y-auto overflow-x-hidden`, so a mark near either
  //      edge had its panel CUT with no scrollbar to reveal it.
  //   3. With no room above it simply ran off the top.
  // The fix is not a new idea, it is the house contract every other anchored panel already uses
  // (popoverPos + a body portal + useCloseOnPageMove) — this was the one panel not using it.
  //
  // 'down' rather than the default 'up': the facts table is a column of rows the agent compares
  // against each other and the mark sits in the middle of it. The rows BELOW are the ones already
  // passed; the rows above are the ones being weighed. Cover the passed ones. Inside the conflict
  // popover the same call applies — being `fixed` it leaves its parent instead of fighting it.
  const openAnchored = () => { cancelClose(); if (ref.current) setPos(popoverPos(ref.current, PANEL_W, 'down')) }

  // ⚠️ THE TAP PATH IS NOT A NICETY. This was hover+focus only, and a touch tap fires a synthetic
  // mouseenter — so it opened and then nothing dismissed it. On a pointer device the sheet never
  // appears; on touch the hover path never runs. One breakpoint, one body, no second component.
  const onActivate = e => {
    e.stopPropagation()   // the cooperation chip owns its own click; the mark must not steal it
    e.preventDefault()
    if (canHover) { pos ? close() : openAnchored() } else setSheet(s => !s)
  }

  return (<>
    {/* The divider draws ITSELF, from position rather than from a prop: `:not(:first-child)`.
        Trailing a value (equipment chip, cooperation chip) it separates mark from word; LEADING
        a row — the conflict popover puts the mark first — there is nothing to separate and no
        rule appears. That is why no call site passes a flag, and why no call site can forget to.
        The seat is 20px inside a 24px tap target: the glyph itself is 8px and was never tappable. */}
    <span ref={ref} className={`relative inline-flex items-center justify-center align-middle h-6 -my-1 ml-0.5 pl-1.5 border-foreground/15 [&:not(:first-child)]:border-l cursor-help ${className}`}
      onMouseEnter={canHover ? openAnchored : undefined}
      onMouseLeave={canHover ? scheduleClose : undefined}
      onFocus={canHover ? openAnchored : undefined}
      onBlur={canHover ? scheduleClose : undefined}
      onClick={onActivate}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onActivate(e) }}
      tabIndex={0} role="button" aria-expanded={!!pos || sheet} aria-label={label}>
      <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${seat}`}>
        <span className={`${hue} text-[0.7em] leading-none select-none`}>{glyph}</span>
      </span>
    </span>

    {pos && createPortal(
      <div ref={panelRef} style={{ ...pos, width: PANEL_W }}
        onMouseEnter={cancelClose} onMouseLeave={scheduleClose} onClick={e => e.stopPropagation()}
        className={`desk-popover fixed z-[200] rounded-lg border ${human ? 'border-primary/40' : 'border-violet-300/40'} bg-popover text-popover-foreground shadow-xl p-2.5 text-xs text-left whitespace-normal overflow-y-auto overscroll-contain`}>
        {body}
      </div>, document.body)}

    {sheet && createPortal(
      <div className="fixed inset-0 z-[200] flex items-end bg-black/50"
        onClick={e => e.stopPropagation()} {...sheetBackdropDismiss}>
        <div className="w-full max-h-[80vh] overflow-y-auto overscroll-contain rounded-t-2xl border-t border-border bg-popover text-popover-foreground p-4 pb-6 text-sm text-left"
          onClick={e => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <MarkBody mark={mark} human={human} hue={hue} glyph={glyph} label={label} big />
            </div>
            <button type="button" onClick={close} aria-label="დახურვა"
              className="shrink-0 w-11 h-11 -mt-1 -mr-1 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted">
              ✕
            </button>
          </div>
        </div>
      </div>, document.body)}
  </>)
}
